import React, { useState, useEffect, useRef } from 'react';
import { Clock, Video, Globe, ChevronLeft, ChevronRight, User, Mail, Phone, Building } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://n8n.custodianai.co.nz/webhook';
const API = `${BACKEND_URL}/api`;

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const ALL_TIME_SLOTS = [
  "09:00 AM", "09:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM", "01:00 PM", "01:30 PM", "02:00 PM", "02:30 PM",
  "03:00 PM", "03:30 PM", "04:00 PM", "04:30 PM", "05:00 PM"
];

const BookingSection = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [currentMonth, setCurrentMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [step, setStep] = useState(1); // 1=calendar, 2=times, 3=form
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [meetLink, setMeetLink] = useState('');
  const refreshIntervalRef = useRef(null);

  // Auto-refresh availability every 30s while viewing time slots
  useEffect(() => {
    if (step === 2 && selectedDate) {
      refreshIntervalRef.current = setInterval(() => fetchAvailability(selectedDate), 30000);
    }
    return () => clearInterval(refreshIntervalRef.current);
  }, [step, selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const toLocalDateStr = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  const fetchAvailability = async (date) => {
    setLoadingSlots(true);
    try {
      const formatted = toLocalDateStr(date);
      const response = await axios.get(`${API}/bookings/availability`, { params: { date: formatted } });
      setAvailableSlots(response.data.available_slots || []);
      setBookedSlots(response.data.booked_slots || []);
    } catch (error) {
      toast.error('Failed to load availability');
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime('');
    fetchAvailability(date);
    setStep(2);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setStep(3);
  };

  const handleBack = () => {
    if (step === 2) { setStep(1); setSelectedDate(null); setAvailableSlots([]); setBookedSlots([]); }
    if (step === 3) { setStep(2); setSelectedTime(''); fetchAvailability(selectedDate); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/bookings`, {
        ...formData,
        date: toLocalDateStr(selectedDate),
        time: selectedTime,
        timezone,
      });
      const link = response.data.meet_link;
      setMeetLink(link || '');
      setStep(4); // confirmation
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'This time slot is already booked');
        setStep(2);
      } else {
        toast.error('Failed to create booking. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setStep(1);
    setSelectedDate(null);
    setSelectedTime('');
    setAvailableSlots([]);
    setBookedSlots([]);
    setMeetLink('');
    setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  // Calendar helpers
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDay = (year, month) => new Date(year, month, 1).getDay();

  const prevMonth = () => {
    const prev = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    if (prev >= new Date(today.getFullYear(), today.getMonth(), 1)) setCurrentMonth(prev);
  };
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDay(year, month);
    const cells = [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

    return (
      <div className="cal-grid">
        {DAYS.map(d => <div key={d} className="cal-day-header">{d}</div>)}
        {cells.map((day, i) => {
          if (!day) return <div key={`e-${i}`} />;
          const thisDate = new Date(year, month, day);
          const isPast = thisDate < today;
          const isSelected = selectedDate && thisDate.toDateString() === selectedDate.toDateString();
          const isToday = thisDate.toDateString() === today.toDateString();
          return (
            <button
              key={day}
              className={`cal-day ${isPast ? 'cal-day-disabled' : 'cal-day-available'} ${isSelected ? 'cal-day-selected' : ''} ${isToday && !isSelected ? 'cal-day-today' : ''}`}
              disabled={isPast}
              onClick={() => handleDateSelect(thisDate)}
            >
              {day}
            </button>
          );
        })}
      </div>
    );
  };

  const formatDate = (date) => date?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <section id="booking" className="section-padding bg-gradient-hero">
      <div className="container">
        <div className="section-header">
          <h2 className="heading-1 text-center">Schedule an Automation Audit</h2>
          <p className="body-large text-center section-subtitle">
            30 minutes. No obligation. A structured assessment of where automation reduces cost in your operations.
          </p>
        </div>

        <div className="caly-container">
          {/* Left info panel */}
          <div className="caly-info-panel">
            <div className="caly-host">
              <div className="caly-avatar">CA</div>
              <span className="caly-host-name">Custodian AI</span>
            </div>
            <h2 className="caly-meeting-title">Automation Audit</h2>
            <p className="caly-meeting-desc">What we cover in 30 minutes:</p>
            <ul className="caly-meeting-bullets">
              <li>Current process inefficiencies and their cost impact</li>
              <li>Highest-ROI automation opportunities in your operations</li>
              <li>Projected cost savings and a deployment overview</li>
            </ul>
            <div className="caly-meta">
              <div className="caly-meta-item"><Clock size={16} /> 30m</div>
              <div className="caly-meta-item"><Video size={16} /> Google Meet</div>
              {selectedDate && selectedTime && step >= 3 && (
                <div className="caly-meta-item caly-selected-time">
                  <Clock size={14} />
                  {selectedTime} · {formatDate(selectedDate)}
                </div>
              )}
              <div className="caly-meta-item"><Globe size={16} /> {timezone}</div>
            </div>
          </div>

          {/* Right interactive panel */}
          <div className="caly-right-panel">
            {/* Step 1: Calendar */}
            {step === 1 && (
              <div className="caly-calendar-step">
                <h3 className="caly-step-title">Select a Date</h3>
                <div className="caly-month-nav">
                  <button onClick={prevMonth} className="caly-nav-btn"><ChevronLeft size={18} /></button>
                  <span className="caly-month-label">{MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}</span>
                  <button onClick={nextMonth} className="caly-nav-btn"><ChevronRight size={18} /></button>
                </div>
                {renderCalendar()}
              </div>
            )}

            {/* Step 2: Time slots */}
            {step === 2 && (
              <div className="caly-time-step">
                <div className="caly-step-header">
                  <button onClick={handleBack} className="caly-back-btn"><ChevronLeft size={16} /> Back</button>
                  <h3 className="caly-step-title">{formatDate(selectedDate)}</h3>
                </div>
                {loadingSlots ? (
                  <div className="caly-loading"><div className="spinner" /></div>
                ) : availableSlots.length > 0 || bookedSlots.length > 0 ? (
                  <div className="caly-time-list">
                    {ALL_TIME_SLOTS.filter(t => availableSlots.includes(t) || bookedSlots.includes(t)).map(time => {
                      const isBooked = bookedSlots.includes(time);
                      return (
                        <button
                          key={time}
                          className={`caly-time-btn${isBooked ? ' caly-time-btn-booked' : ''}`}
                          onClick={() => !isBooked && handleTimeSelect(time)}
                          disabled={isBooked}
                          title={isBooked ? 'This time slot is already booked' : ''}
                        >
                          {isBooked ? <s>{time}</s> : time}
                          {isBooked && <span className="caly-time-booked-label">Booked</span>}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="caly-no-slots">No available slots for this date. Please go back and choose another day.</p>
                )}
              </div>
            )}

            {/* Step 3: Form */}
            {step === 3 && (
              <div className="caly-form-step">
                <div className="caly-step-header">
                  <button onClick={handleBack} className="caly-back-btn"><ChevronLeft size={16} /> Back</button>
                  <h3 className="caly-step-title">Enter Your Details</h3>
                </div>
                <div className="caly-booking-summary">
                  <Clock size={14} />
                  <span>{selectedTime} · {formatDate(selectedDate)}</span>
                </div>
                <form onSubmit={handleSubmit} className="caly-form">
                  <div className="form-group">
                    <label className="form-label"><User size={14} /> Full Name *</label>
                    <input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="form-input" placeholder="John Smith" required />
                  </div>
                  <div className="form-group">
                    <label className="form-label"><Mail size={14} /> Email Address *</label>
                    <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="form-input" placeholder="john@company.com" required />
                  </div>
                  <div className="form-group">
                    <label className="form-label"><Phone size={14} /> Phone Number *</label>
                    <input type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="form-input" placeholder="+64 21 234 5678" required />
                  </div>
                  <div className="form-group">
                    <label className="form-label"><Building size={14} /> Company Name</label>
                    <input type="text" value={formData.company} onChange={e => setFormData({...formData, company: e.target.value})} className="form-input" placeholder="Your Company" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tell us about your business</label>
                    <textarea value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})} className="form-textarea" placeholder="Brief description of your business and what you're looking to achieve..." rows="3" />
                  </div>
                  <button type="submit" className="btn-primary btn-large btn-full hover-scale" disabled={isSubmitting}>
                    {isSubmitting ? 'BOOKING...' : 'CONFIRM BOOKING'}
                  </button>
                </form>
              </div>
            )}

            {/* Step 4: Confirmation */}
            {step === 4 && (
              <div className="caly-confirm-step">
                <div className="caly-confirm-icon">✓</div>
                <h3 className="caly-confirm-title">Booking Confirmed!</h3>
                <p className="caly-confirm-sub">
                  Your call is scheduled for <strong>{selectedTime}</strong> on <strong>{formatDate(selectedDate)}</strong>.
                </p>
                {meetLink && (
                  <a href={meetLink} target="_blank" rel="noopener noreferrer" className="caly-meet-link">
                    <Video size={16} /> Join Google Meet
                  </a>
                )}
                <p className="caly-confirm-note">A confirmation email has been sent to you with all the details.</p>
                <button onClick={handleReset} className="caly-new-booking-btn">Book Another Call</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
